import { css } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { type LoginDeliveryMethod, type LoginError, useLoginWithCode } from '@kaiizen/kai-pro-api-react-state';

import { ANALYTICS_EVENTS, trackEvent } from '../analytics';
import AuthHero from '../Assets/auth-hero.jpg?url';
import { KaiLogoWithName } from '../Icons';
import { useAuth } from '../State';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../Styles';
import { Button, LabeledInput } from '../Widgets';

const MOBILE_BREAKPOINT = 900;
const BACKGROUND_IMAGE_PATH = AuthHero;

const TITLE_STYLE = css`
  label: TITLE_STYLE;
  ${TYPOGRAPHY2.xxLargeBold}
  color: var(--text);
  margin-top: ${LAYOUT2.spacing1}px;
  margin-bottom: ${LAYOUT2.spacing1}px;
  text-align: left;
`;

const PAGE_CONTAINER_STYLE_NORMAL = css`
  ${TYPOGRAPHY2.xSmall}
  label: PAGE_CONTAINER_STYLE_NORMAL;
  position: 'relative' as const;
  display: 'flex';
  height: '100vh';
  width: '100vw';
  overflow: 'hidden';
  background: var(--background);
}
`;

const PAGE_CONTAINER_STYLE_MOBILE = css`
  ${TYPOGRAPHY2.xSmall}
  label: PAGE_CONTAINER_STYLE_MOBILE;
  width: '100vw';
  min-height: '100vh';
  background: var(--background);
}
`;

const ERROR_STYLE = css`
  label: ERROR_STYLE;
  ${TYPOGRAPHY2.smallBold}
  padding: ${LAYOUT2.spacing1}px;
  color: ${COLORS2.alwaysDark.blackTransparent6};
`;

const MOBILE_TOP_BANNER_STYLE = css`
  label: MOBILE_TOP_BANNER_STYLE;
  width: 100vw;
  height: 300px;
  position: relative;
  margin-bottom: 20px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
`;

const MOBILE_TOP_BANNER_BG_STYLE = css`
  label: MOBILE_TOP_BANNER_BG_STYLE;
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100vw;
  height: 300px;
  background-image: url(${BACKGROUND_IMAGE_PATH});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
`;

const INSTRUCTIONS_STYLE = css`
  color: var(--text2);
  ${TYPOGRAPHY2.large}
  margin-bottom: var(--spacing1Px);
`;
const RIGHT_SIDE_STYLE = css`
  label: RIGHT_SIDE_STYLE;
  position: absolute;
  top: 0;
  right: 0;
  width: 50vw;
  height: 100vh;
  background-image: url(${BACKGROUND_IMAGE_PATH});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const BODY_WRAPPER_STYLE = css`
  label: BODY_WRAPPER_STYLE;
  display: flex;
  color: var(--text);
  flex-direction: column;
  gap: ${LAYOUT2.spacing2}px;
  align-items: flex-start;
  justify-content: flex-start;
`;

const LEFT_SIDE_STYLE_MOBILE = css`
  label: LEFT_SIDE_STYLE_MOBILE;
  width: 100vw;
  min-height: calc(100vh - 200px);
  background: var(--background);
  overflow-y: auto;
  display: block;
  padding: 20px;
`;

const LEFT_SIDE_STYLE_TALL = css`
  label: LEFT_SIDE_STYLE_TALL;
  width: 50vw;
  height: 100vh;
  background: var(--background);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LEFT_SIDE_STYLE_NORMAL = css`
  label: LEFT_SIDE_STYLE_NORMAL;
  width: 50vw;
  height: 100vh;
  background: var(--background);
  overflow-y: auto;
  display: block;
`;

const LOGIN_ERROR_CONTAINER_BY_SEVERITY = {
  error: css`
    background-color: ${COLORS2.alwaysDark.offWhiteRed};
    border: 1px solid ${COLORS2.alwaysDark.red};
  `,
  warning: css`
    background-color: ${COLORS2.alwaysDark.offWhiteYellow};
    border: 1px solid ${COLORS2.alwaysDark.yellow};
  `,
};

const RESEND_STYLE = css`
  label: RESEND_STYLE;
  ${TYPOGRAPHY2.medium}
  color: var(--text2);
`;

const RESEND_LINK_STYLE = css`
  label: RESEND_LINK_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  border: 0;
  padding: 0;
  background: none;
  color: var(--text);
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    color: var(--text2);
    text-decoration: none;
    cursor: default;
  }
`;

/** Reports each failed login request, once, to analytics. */
const useTrackLoginFailures = (loginError: LoginError | undefined) => {
  useEffect(() => {
    if (loginError) trackEvent(ANALYTICS_EVENTS.userLoginFailure, { deliveryMethod: loginError.deliveryMethod, stage: loginError.stage });
  }, [loginError]);
};

/** Reports each resend that went out, once, to analytics. */
const useTrackCodeResends = (codeResentAt: number | undefined, deliveryMethod: LoginDeliveryMethod | undefined) => {
  useEffect(() => {
    if (codeResentAt !== undefined) trackEvent(ANALYTICS_EVENTS.userLoginCodeResent, { deliveryMethod });
  }, [codeResentAt, deliveryMethod]);
};

export const Auth = () => {
  const [isTall, setIsTall] = useState(window.innerHeight >= 650);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const navigate = useNavigate();

  const { credentials } = useAuth();
  const {
    contact,
    setContact,
    authCode,
    setAuthCode,
    codeContact,
    codeDeliveryMethod,
    sendingCode,
    verifyingCode,
    requestPending,
    canRequestCode,
    canVerifyCode,
    resendOffered,
    canResendCode,
    secondsUntilResend,
    codeResentAt,
    loginError,
    requestCode,
    resendCode,
    verifyCode,
    changeContact,
  } = useLoginWithCode();
  useTrackLoginFailures(loginError);
  useTrackCodeResends(codeResentAt, codeDeliveryMethod);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
      setIsTall(window.innerHeight >= 650);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (credentials.token) navigate('/');
  }, [credentials.token, navigate]);

  const sentVerb = codeDeliveryMethod === 'email' ? 'emailed' : 'texted';

  const PAGE_CONTAINER_STYLE = isMobile
    ? PAGE_CONTAINER_STYLE_MOBILE
    : PAGE_CONTAINER_STYLE_NORMAL;

  const LEFT_SIDE_STYLE = isMobile
    ? LEFT_SIDE_STYLE_MOBILE
    : isTall
    ? LEFT_SIDE_STYLE_TALL
    : LEFT_SIDE_STYLE_NORMAL;

  const FORM_WRAPPER_STYLE = isMobile
    ? css`
      width: 100%;
      padding: ${LAYOUT2.spacing1}px;
    `
    : css`
      max-width: 420px;
      margin: 0 auto;
      padding-top: ${LAYOUT2.spacing3}px;
      padding-bottom: ${LAYOUT2.spacing3}px;
    `;

  return (
    <div css={PAGE_CONTAINER_STYLE}>
      {isMobile
        ? (
          <div css={MOBILE_TOP_BANNER_STYLE}>
            <div css={MOBILE_TOP_BANNER_BG_STYLE} />
          </div>
        )
        : null}
      <div css={LEFT_SIDE_STYLE}>
        <div css={FORM_WRAPPER_STYLE}>
          <div css={BODY_WRAPPER_STYLE}>
            <KaiLogoWithName />
            <div css={TITLE_STYLE}>Welcome to Kai, the AI-Powered Renovation Platform</div>
            {codeContact !== undefined
              ? (
                <>
                  <div css={[INSTRUCTIONS_STYLE, { marginBottom: 0 }]}>
                    Enter the 6-digit code we {sentVerb} to {codeContact} below to finish signing in.
                  </div>
                  <LabeledInput
                    label='Verification Code'
                    type='number'
                    size='large'
                    placeholder='123456'
                    value={authCode}
                    onChange={setAuthCode}
                    autoComplete='one-time-code'
                    disabled={requestPending}
                    onEnter={canVerifyCode ? verifyCode : undefined}
                  />
                  <Button onClick={verifyCode} size='large' width='100%' disabled={!canVerifyCode}>
                    {verifyingCode ? 'Verifying...' : 'Verify Code'}
                  </Button>
                  {resendOffered && (
                    <div css={RESEND_STYLE} role='status'>
                      {codeResentAt === undefined ? "Didn't get a code? " : `New code ${sentVerb} to ${codeContact}. `}
                      {secondsUntilResend > 0
                        ? `You can resend in ${secondsUntilResend}s.`
                        : (
                          <button type='button' css={RESEND_LINK_STYLE} disabled={!canResendCode} onClick={resendCode}>
                            {sendingCode ? 'Sending...' : 'Resend'}
                          </button>
                        )}
                    </div>
                  )}
                  <Button onClick={changeContact} size='medium' buttonStyle='white' width='100%'>
                    Wrong phone or email?
                  </Button>
                </>
              )
              : (
                <>
                  <div css={INSTRUCTIONS_STYLE}>Please log in with your email or phone number to continue.</div>
                  <LabeledInput
                    label={'Email or phone number'}
                    size='large'
                    value={contact}
                    onChange={setContact}
                    autoComplete={contact.includes('@') ? 'email' : 'tel'}
                    disabled={requestPending}
                    onEnter={canRequestCode ? requestCode : undefined}
                    placeholder='user@example.com or 1234567890'
                  />
                  <Button onClick={requestCode} size='large' width='100%' disabled={!canRequestCode}>
                    {sendingCode ? 'Sending verification code...' : 'Continue'}
                  </Button>
                </>
              )}
            {loginError && <div css={[LOGIN_ERROR_CONTAINER_BY_SEVERITY[loginError.severity], ERROR_STYLE]}>{loginError.message}</div>}
          </div>
        </div>
      </div>
      {!isMobile && <div css={RIGHT_SIDE_STYLE} />}
    </div>
  );
};
